import React, { useEffect } from 'react';
import { loadTrendData } from '../services/trendDataService';
import { TrendPairData } from '../types';

/**
 * Props for the DataLoader component
 */
interface DataLoaderProps {
  /**
   * Callback function to execute when data is successfully loaded
   * This function receives the loaded trend pairs array
   */
  onDataLoaded: (data: TrendPairData[]) => void;
}

/**
 * DataLoader: A component that handles loading data without rendering UI
 * 
 * This component demonstrates an important React pattern - a component
 * that exists solely to handle data fetching, not to render UI.
 * 
 * This pattern is useful for:
 * - Separating data loading logic from presentation components
 * - Reusing data fetching logic across multiple components
 * - Keeping components focused on a single responsibility
 * 
 * The component fetches trend data when mounted and passes the result
 * to its parent component through the onDataLoaded callback.
 */
const DataLoader: React.FC<DataLoaderProps> = ({ onDataLoaded }) => {
  useEffect(() => {
    /**
     * This effect runs when the component mounts, initiating data loading
     * 
     * The useEffect hook is perfect for side effects like data fetching.
     * We configure it to run only when the component mounts or if the 
     * onDataLoaded callback changes.
     */
    
    const loadData = async () => {
      try {
        // Call the service to load trend data from the CSV file
        const data = await loadTrendData();
        
        // Check if we got any data back
        if (data.length === 0) {
          console.error('No trend data was loaded.');
          return;
        }
        
        // Pass the loaded data to the parent component via callback
        onDataLoaded(data);
      } catch (error) {
        console.error('Failed to load trend data:', error);
      }
    };
    
    // Execute the loading function
    loadData();
    
  }, [onDataLoaded]); // Dependency array controls when effect runs

  // This component doesn't render any visible UI
  return null;
};

export default DataLoader;